// A project's board: its tasks and its coding agents in four columns, from
// what the app already knows (task state, the agent a task was handed to, the
// agent's pull request). Pure; internal/assistant/board.go is the same rules
// for Prime's board tool, keep them in step.
import type { TaskResource } from '@/kinds/task/apply'
import type { AgentRunResource } from '@/kinds/agentrun/apply'

export type Column = 'todo' | 'agent' | 'review' | 'done'
export const COLUMNS: { id: Column; title: string }[] = [
  { id: 'todo', title: 'To do' },
  { id: 'agent', title: 'With an agent' },
  { id: 'review', title: 'In review' },
  { id: 'done', title: 'Done' },
]

export interface BoardCard {
  key: string
  /** the project it sits in (the board of several projects tags each card) */
  project?: string
  task?: TaskResource
  run?: AgentRunResource
}

/** The task an agent works for: the one it started from, else the one it was tied to (link_task). */
export const taskOf = (r: AgentRunResource): string | undefined =>
  (r.spec.source_kind === 'task' ? r.spec.source_ref : undefined) || r.status.task_id || undefined

const over = (phase: string) => phase === 'Ended' || phase === 'Failed'
const DONE_DAYS = 14

/** Where an agent's work stands: review while its PR is open, done once merged. */
const runColumn = (r: AgentRunResource): Column | null => {
  if (r.spec.pr_number && r.spec.pr_state === 'merged') return 'done'
  if (r.spec.pr_number && (r.spec.pr_state ?? 'open') === 'open') return 'review'
  if (!over(r.spec.status)) return 'agent'
  return null
}

/**
 * The board of one project, of several, or of all (`scope` null). `tasks` are
 * the tasks to lay out and `runs` every coding agent. With a `projectOf` map
 * (task id → project id) a task counts only when its project is in scope, and
 * agents with no task or project show too when the scope is all ("no task").
 * Without one, every task given is taken to belong to the single project named.
 * Done keeps the last fortnight; dropped tasks and agents that ended with
 * nothing to show are left off.
 */
export const boardOf = (
  scope: string | string[] | null,
  tasks: TaskResource[],
  runs: AgentRunResource[],
  now: number,
  projectOf?: Map<string, string>,
): Record<Column, BoardCard[]> => {
  const ids = scope === null ? null : new Set(typeof scope === 'string' ? [scope] : scope)
  const single = typeof scope === 'string' ? scope : undefined
  const inScope = (p: string | undefined) => (ids ? !!p && ids.has(p) : true)
  const out: Record<Column, BoardCard[]> = { todo: [], agent: [], review: [], done: [] }
  const since = new Date(now - DONE_DAYS * 86400_000).toISOString()
  const live = runs.filter((r) => !r.deleted_at && r.spec.template !== 'prime')
  // a task's agent: the newest one it was handed to
  const byTask = new Map<string, AgentRunResource>()
  for (const r of live) {
    const tid = taskOf(r)
    if (!tid) continue
    const had = byTask.get(tid)
    if (!had || (r.spec.updated_at ?? '') > (had.spec.updated_at ?? '')) byTask.set(tid, r)
  }
  const taskIds = new Set<string>()
  for (const t of tasks) {
    if (t.deleted_at) continue
    const project = projectOf ? projectOf.get(t.id) : single
    if (projectOf && (!project || !inScope(project))) continue
    taskIds.add(t.id)
    const st = t.status.state
    if (st === 'dropped') continue
    const run = byTask.get(t.id)
    if (st === 'done') {
      if ((t.status.completed_at ?? '') >= since) out.done.push({ key: t.id, project, task: t, run })
      continue
    }
    const col = run ? runColumn(run) : null
    out[col ?? 'todo'].push({ key: t.id, project, task: t, run })
  }
  // agents started on the project with no task of it
  for (const r of live) {
    const tid = taskOf(r)
    if (tid && taskIds.has(tid)) continue
    const project = r.spec.project_id || (tid ? projectOf?.get(tid) : undefined) || undefined
    if (!inScope(project)) continue
    const col = runColumn(r)
    if (!col) continue
    if (col === 'done' && (r.spec.updated_at ?? '') < since) continue
    out[col].push({ key: r.id, project, run: r })
  }
  out.done.sort((a, b) => {
    const at = (c: BoardCard) => c.task?.status.completed_at ?? c.run?.spec.updated_at ?? ''
    return at(b) < at(a) ? -1 : 1
  })
  return out
}
